import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  getSelectOwnerHelpMessage,
  OwnershipType,
  shouldHideSelectOwnerField,
} from 'create-components/OwnableUtil';
import SelectOwnerField from 'create-components/SelectOwnerField';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import Row from 'ui-library/bs/Row';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import useFormInput, {ControlledInputProps} from 'ui-library/hooks/useFormInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';

import {MAX_NAME_LENGTH} from './WorklistConstants';

export function useNewWorklistForm(): {
  name: ControlledInputProps;
  ownerId: string;
} {
  const name = useFormInput('');
  const ownerId = EntityStore.currentUser().id;
  return {
    name,
    ownerId,
  };
}

export interface NewWorklistFormProps {
  name: ControlledInputProps;
  ownerId: string;
}

/**
 * Controlled component to create a new worklist. Works well with useNewWorklistForm.
 */
export default function NewWorklistForm(props: NewWorklistFormProps): JSX.Element {
  const owner = EntityStore.currentUser();
  const helpMessage = getSelectOwnerHelpMessage(ModelTypeConstants.WORKLIST, false, OwnershipType.CREATOR);
  return (
    <FormGroup className={css(styles.grayBackground)}>
      <Row>
        <Col sm={shouldHideSelectOwnerField() ? 12 : 6}>
          <FormGroup>
            <FieldLabel label="Worklist Name" labelStyle={LabelStyle.SUBLABEL} isRequired={true} />
            <FormInput
              {...props.name}
              autoFocus={true}
              placeholder="Name"
              maxLength={MAX_NAME_LENGTH}
              {...getTestProps({component: 'NewWorklistForm', element: 'input'})}
            />
          </FormGroup>
        </Col>
        {!shouldHideSelectOwnerField() && (
          <Col sm={6}>
            <Tooltip
              title="Worklists are always owned by their creator"
              placement={TooltipPlacement.BOTTOM}
              container="body"
              disabled={false}
            >
              <SelectOwnerField
                ownableType={ModelTypeConstants.WORKLIST}
                isReadOnly={true}
                ownerId={props.ownerId}
                selectOwner={(newOwnerId) => {}}
                potentialOwners={[owner]}
                collaboratorsAllowed={false}
                helpMessage={helpMessage}
                useFieldSubLabelStyle={true}
              />
            </Tooltip>
          </Col>
        )}
      </Row>
    </FormGroup>
  );
}

const styles = StyleSheet.create({
  grayBackground: {
    backgroundColor: GRAY_LIGHTER,
    borderRadius: 4,
    padding: 12,
    paddingBottom: 0,
  },
});
